import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { inlineMediaUrl } from '../inlineMediaUrl';
import { releaseThemeMessages } from '../themes/releaseThemeMessages';
import { RevisionEntry } from './revisionModel';
import { revisionMessages } from './revisionMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Revisions.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RevisionCompareSideProps {
  /** Undefined: no revision is picked for this side yet. */
  entry: RevisionEntry | undefined;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
}

/** One side of Compare (design d2): "r4 · Noir · 7 Oct" and the file. A video has no own controls: one button plays both. */
const RevisionCompareSideComponent: React.FC<RevisionCompareSideProps> = ({ entry, videoRef }) => {
  const intl = useIntl();
  const item = entry?.viewerItem ?? null;
  if (entry === undefined || item === null) {
    return (
      <div className={styles.compareSide}>
        <span className={styles.goneNote}>{intl.formatMessage(revisionMessages.compareHint)}</span>
      </div>
    );
  }
  const label = [
    intl.formatMessage(revisionMessages.number, { number: entry.number }),
    intl.formatMessage(releaseThemeMessages[entry.releaseThemeId].name),
    intl.formatDate(entry.madeAt, { day: 'numeric', month: 'short' }),
  ].join(' · ');

  return (
    <div className={styles.compareSide} data-testid={`compare-side-r${entry.number}`}>
      <span className={styles.compareLabel}>{label}</span>
      {item.mediaKind === 'image' ? (
        <img className={styles.compareMedia} src={inlineMediaUrl(item.fileUrl)} alt={label} />
      ) : (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- a made asset has no speech track to caption
        <video
          ref={videoRef}
          className={styles.compareMedia}
          src={inlineMediaUrl(item.playUrl)}
          poster={item.posterUrl === null ? undefined : inlineMediaUrl(item.posterUrl)}
          aria-label={label}
          playsInline
          muted
          preload="metadata"
          loop={item.loop}
        />
      )}
    </div>
  );
};

export const RevisionCompareSide = TypedMemo(RevisionCompareSideComponent);
